import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Kontrolltermin, KontrollIndikator } from '../../../services/kontrolltermin.service';

interface ProduktSummary {
  id: string;
  name: string;
  description?: string;
  totalAmount: number;
  usedInIndicators: string[]; // Indicator locations where used
}

@Component({
  selector: 'app-eingesetzte-produkte-dialog',
  templateUrl: './eingesetzte-produkte-dialog.component.html',
  styleUrls: ['./eingesetzte-produkte-dialog.component.scss']
})
export class EingesetzteProdukteDialogComponent implements OnInit {
  kontrolltermin: Kontrolltermin;
  produktSummary: ProduktSummary[] = [];
  displayedColumns: string[] = ['produkt', 'menge', 'verwendet'];
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<EingesetzteProdukteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { kontrolltermin: Kontrolltermin }
  ) {
    console.log('🔘 EingesetzteProdukteDialogComponent constructor');
    console.log('📊 Data received:', data);
    
    if (!data || !data.kontrolltermin) {
      console.error('❌ No kontrolltermin in dialog data');
      this.kontrolltermin = {} as Kontrolltermin;
      return;
    }
    
    this.kontrolltermin = data.kontrolltermin;
    console.log('📊 Kontrolltermin set:', this.kontrolltermin);
  }

  ngOnInit() {
    console.log('🔘 EingesetzteProdukteDialogComponent ngOnInit');
    console.log('📊 Kontrolltermin in ngOnInit:', this.kontrolltermin);
    this.calculateProduktSummary();
  }

  calculateProduktSummary() {
    console.log('🔘 calculateProduktSummary called');
    const produktMap = new Map<string, ProduktSummary>();
    const indicators = this.kontrolltermin.kontrollIndikatoren || this.kontrolltermin.kontroll_indikatoren || [];
    
    console.log('📊 Indicators found:', indicators.length);
    console.log('📊 Indicators data:', indicators);

    indicators.forEach((indicator: KontrollIndikator) => {
      console.log('📊 Processing indicator:', indicator);
      const artikel = this.getArtikelFromIndicator(indicator);
      
      artikel.forEach(item => {
        const produktId = item.id || item.name;
        const produktName = item.name || item.toString();
        const amount = item.amount || 1;
        const location = indicator.room_name || indicator.indikator_name || 'Unbekannt';

        if (produktMap.has(produktId)) {
          const existing = produktMap.get(produktId)!;
          existing.totalAmount += amount;
          if (!existing.usedInIndicators.includes(location)) {
            existing.usedInIndicators.push(location);
          }
        } else {
          produktMap.set(produktId, {
            id: produktId,
            name: produktName,
            description: item.description,
            totalAmount: amount,
            usedInIndicators: [location]
          });
        }
      });
    });

    // Convert map to array and sort by name
    this.produktSummary = Array.from(produktMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  getArtikelFromIndicator(indicator: KontrollIndikator): any[] {
    if (!indicator.interne_notiz) return [];
    try {
      const parsed = JSON.parse(indicator.interne_notiz);
      if (Array.isArray(parsed)) {
        return parsed.map(item => ({
          id: item.id || 'unknown',
          name: item.name || item.toString(),
          description: item.description,
          amount: item.amount || 1
        }));
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  printProdukte() {
    const printContent = this.generatePrintHTML();
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) return;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }

  generatePrintHTML(): string {
    const termin = this.kontrolltermin;
    const standort = termin.standort?.name || 'Unbekannt';
    const kunde = termin.kunde?.suchbegriff || 'Unbekannt';
    const datum = termin.datum_durchfuerung 
      ? new Date(termin.datum_durchfuerung).toLocaleDateString('de-DE')
      : 'Nicht angegeben';
    const einsatznummer = termin.einsatznummer || 'N/A';

    // Logo path (adjust based on your assets)
    const logoPath = '/assets/sbk1-logo.png';

    let tableRows = '';
    if (this.produktSummary.length === 0) {
      tableRows = '<tr><td colspan="3" style="text-align: center; padding: 20px;">Keine Produkte verwendet</td></tr>';
    } else {
      tableRows = this.produktSummary.map(produkt => `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #ddd;">${produkt.name}</td>
          <td style="padding: 12px; border-bottom: 1px solid #ddd; text-align: center;">${produkt.totalAmount}</td>
          <td style="padding: 12px; border-bottom: 1px solid #ddd; font-size: 12px; color: #666;">${produkt.usedInIndicators.join(', ')}</td>
        </tr>
      `).join('');
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Eingesetzte Produkte - ${einsatznummer}</title>
        <style>
          @media print {
            @page {
              margin: 20mm;
            }
            body {
              margin: 0;
              padding: 0;
            }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
          }
          .logo {
            max-width: 200px;
            height: auto;
            margin-bottom: 20px;
          }
          .title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .info-section {
            margin-bottom: 30px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .info-label {
            font-weight: bold;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #3b82f6;
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: bold;
          }
          td {
            padding: 12px;
            border-bottom: 1px solid #ddd;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${logoPath}" alt="SBK1 Logo" class="logo" onerror="this.style.display='none'">
          <div class="title">Eingesetzte Produkte</div>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Auftragsnummer:</span>
            <span>${einsatznummer}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Standort:</span>
            <span>${standort}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Kunde:</span>
            <span>${kunde}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Datum:</span>
            <span>${datum}</span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Produkt</th>
              <th style="text-align: center; width: 100px;">Menge</th>
              <th>Verwendet in</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <div class="footer">
          <p>Erstellt am: ${new Date().toLocaleString('de-DE')}</p>
          <p>SBK1 SmartHACCP Web App</p>
        </div>
      </body>
      </html>
    `;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'Nicht angegeben';
    return new Date(dateString).toLocaleDateString('de-DE');
  }
}

